import { useEffect } from "react";
import { motion, useMotionValue, useTransform, useReducedMotion, animate } from "framer-motion";

// A living procedural scene that evolves from night/seed -> morning/lush tree
// as `progress` (0..1) grows. Zero AI cost — pure SVG + Framer Motion.
const BLOSSOMS = [
  { cx: 180, cy: 92, r: 3.4, t: 0.74 }, { cx: 214, cy: 84, r: 3, t: 0.77 },
  { cx: 160, cy: 116, r: 3, t: 0.8 }, { cx: 236, cy: 112, r: 3.2, t: 0.82 },
  { cx: 198, cy: 70, r: 2.8, t: 0.84 }, { cx: 176, cy: 134, r: 3, t: 0.86 },
  { cx: 226, cy: 136, r: 2.8, t: 0.88 }, { cx: 200, cy: 120, r: 3.2, t: 0.9 },
  { cx: 150, cy: 100, r: 2.6, t: 0.92 }, { cx: 250, cy: 100, r: 2.6, t: 0.94 },
];

export default function FocusJourneyScene({ progress = 0, heightClass = "h-[62vh] min-h-[460px] max-h-[640px]" }) {
  const reduce = useReducedMotion();
  const mp = useMotionValue(reduce ? progress : 0);

  useEffect(() => {
    if (reduce) {
      mp.set(progress);
      return;
    }
    const controls = animate(mp, progress, { duration: 2.4, ease: [0.22, 1, 0.36, 1] });
    return () => controls.stop();
  }, [progress, reduce, mp]);

  // sky gradient stops — starts at a warm, hopeful dawn (not midnight) and
  // brightens into full morning as progress grows.
  const skyTop = useTransform(mp, [0, 0.3, 0.6, 1], ["#243056", "#2C3A66", "#3E5A8E", "#5E8FC2"]);
  const skyMid = useTransform(mp, [0, 0.3, 0.6, 1], ["#6E5A86", "#9A6A66", "#C2865A", "#E0A85C"]);
  const skyBot = useTransform(mp, [0, 0.3, 0.6, 1], ["#E0975A", "#EBAE5C", "#F2C75E", "#F8DA82"]);

  const starsOpacity = useTransform(mp, [0, 0.2, 0.4], [0.4, 0.18, 0]);
  const moonOpacity = useTransform(mp, [0, 0.18], [0.35, 0]);
  const sunY = useTransform(mp, [0, 0.65, 1], [232, 196, 70]);
  const sunOpacity = useTransform(mp, [0, 0.5, 1], [0.35, 0.7, 1]);

  const trunkLen = useTransform(mp, [0, 0.16], [0, 1]);
  const seedOpacity = useTransform(mp, [0, 0.09, 0.16], [1, 1, 0]);
  const canopyScale = useTransform(mp, [0.16, 0.7], [0.16, 1]);
  const canopyOpacity = useTransform(mp, [0.16, 0.3], [0, 1]);
  const riverOpacity = useTransform(mp, [0, 0.6, 1], [0.12, 0.5, 0.85]);
  const birdsOpacity = useTransform(mp, [0.62, 0.85], [0, 1]);

  return (
    <div
      data-testid="focus-journey-scene"
      role="img"
      aria-label="Your Focus Journey — a tree growing under a changing sky that reflects your reading progress"
      className={`relative ${heightClass} w-full overflow-hidden rounded-[1.75rem] border border-primary/15 shadow-[0_24px_60px_-24px_rgba(212,175,55,0.35)]`}
    >
      <svg viewBox="0 0 400 280" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full">
        <defs>
          <linearGradient id="fj-sky" x1="0" y1="0" x2="0" y2="1">
            <motion.stop offset="0%" stopColor={skyTop} />
            <motion.stop offset="55%" stopColor={skyMid} />
            <motion.stop offset="100%" stopColor={skyBot} />
          </linearGradient>
          <radialGradient id="fj-sun" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FFF3D0" />
            <stop offset="55%" stopColor="#F2C14E" />
            <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="fj-foliage" cx="36%" cy="26%" r="82%">
            <stop offset="0%" stopColor="#A6D26F" />
            <stop offset="45%" stopColor="#5E9A4E" />
            <stop offset="100%" stopColor="#2C5836" />
          </radialGradient>
          <radialGradient id="fj-foliage-hi" cx="40%" cy="30%" r="70%">
            <stop offset="0%" stopColor="#E2F2A6" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#A6D26F" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="fj-bark" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#352616" />
            <stop offset="48%" stopColor="#5C4329" />
            <stop offset="100%" stopColor="#2A1D12" />
          </linearGradient>
          <radialGradient id="fj-blossom" cx="50%" cy="40%" r="60%">
            <stop offset="0%" stopColor="#FFF6E8" />
            <stop offset="55%" stopColor="#F6C8C0" />
            <stop offset="100%" stopColor="#E89AA0" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="fj-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FFE6A8" stopOpacity="0.55" />
            <stop offset="55%" stopColor="#F2C14E" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#F2C14E" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="fj-ground" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2C4A2E" />
            <stop offset="100%" stopColor="#13241A" />
          </linearGradient>
          <linearGradient id="fj-river" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#E7C25C" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#3E5A6B" stopOpacity="0.7" />
          </linearGradient>
          <filter id="fj-soft"><feGaussianBlur stdDeviation="4" /></filter>
        </defs>

        {/* sky */}
        <rect x="0" y="0" width="400" height="280" fill="url(#fj-sky)" />

        {/* stars */}
        <motion.g style={{ opacity: starsOpacity }} fill="#FDEBD0">
          {[[40, 40], [90, 28], [150, 52], [220, 34], [300, 50], [350, 30], [270, 70], [120, 80], [330, 90], [60, 70]].map(
            ([x, y], i) => <circle key={i} cx={x} cy={y} r={i % 3 === 0 ? 1.3 : 0.8} />
          )}
        </motion.g>

        {/* moon */}
        <motion.circle style={{ opacity: moonOpacity }} cx="320" cy="58" r="13" fill="#D7D7E4" />

        {/* sun */}
        <motion.g style={{ opacity: sunOpacity }}>
          <motion.circle cx="300" style={{ cy: sunY }} r="46" fill="url(#fj-sun)" />
          <motion.circle cx="300" style={{ cy: sunY }} r="20" fill="#FFF3D0" />
        </motion.g>

        {/* distant hills */}
        <path d="M0 226 Q 90 200 180 220 T 400 212 V280 H0 Z" fill="#1B2A40" opacity="0.7" />
        {/* ground */}
        <path d="M0 236 Q 120 216 250 234 T 400 230 V280 H0 Z" fill="url(#fj-ground)" />
        <path d="M0 252 Q 140 238 280 250 T 400 248 V280 H0 Z" fill="#0E1C14" />

        {/* river */}
        <motion.g style={{ opacity: riverOpacity }}>
          <motion.path
            d="M-40 262 Q 60 254 160 262 T 360 262 T 560 262 V280 H-40 Z"
            fill="url(#fj-river)"
            animate={reduce ? undefined : { x: [0, -200] }}
            transition={reduce ? undefined : { repeat: Infinity, duration: 14, ease: "linear" }}
          />
        </motion.g>

        {/* seedling — the starting point ("planting the seed"), tall & glowing */}
        <motion.g style={{ opacity: seedOpacity }}>
          <motion.circle
            cx="200" cy="135" r="74" fill="url(#fj-glow)"
            animate={reduce ? undefined : { opacity: [0.5, 0.9, 0.5], scale: [0.92, 1.08, 0.92] }}
            transition={reduce ? undefined : { repeat: Infinity, duration: 3.2, ease: "easeInOut" }}
            style={{ transformBox: "fill-box", transformOrigin: "center" }}
          />
          <ellipse cx="200" cy="228" rx="42" ry="9" fill="#1B3324" />
          <motion.g
            animate={reduce ? undefined : { rotate: [-2.5, 2.5, -2.5] }}
            transition={reduce ? undefined : { repeat: Infinity, duration: 4.5, ease: "easeInOut" }}
            style={{ transformBox: "view-box", transformOrigin: "200px 228px" }}
          >
            <path d="M200 228 C 198 192, 200 150, 200 104" stroke="#6AA85A" strokeWidth="5" fill="none" strokeLinecap="round" />
            <path d="M200 138 C 172 124, 148 128, 144 148 C 167 158, 193 152, 200 138 Z" fill="url(#fj-foliage)" />
            <path d="M200 126 C 228 112, 252 116, 256 136 C 233 146, 207 140, 200 126 Z" fill="url(#fj-foliage)" />
            <path d="M200 164 C 179 156, 160 159, 157 174 C 175 180, 193 177, 200 164 Z" fill="url(#fj-foliage)" opacity="0.92" />
            <circle cx="200" cy="98" r="7.5" fill="url(#fj-blossom)" />
          </motion.g>
        </motion.g>

        {/* trunk + limbs (grow up from the ground) */}
        <motion.g style={{ scaleY: trunkLen, transformBox: "fill-box", transformOrigin: "bottom" }}>
          <path
            d="M193 252 C 192 226, 190 208, 195 178 C 196 170, 199 162, 200 152 C 201 162, 204 170, 205 178 C 210 208, 208 226, 207 252 Z"
            fill="url(#fj-bark)"
          />
          <path d="M193 252 C 187 247, 180 247, 173 251 L 197 252 Z" fill="#241910" opacity="0.85" />
          <path d="M207 252 C 213 247, 221 247, 228 251 L 203 252 Z" fill="#241910" opacity="0.85" />
          <g fill="url(#fj-bark)">
            <path d="M199 180 C 184 172, 170 164, 156 152 C 168 166, 184 174, 200 184 Z" />
            <path d="M201 174 C 216 166, 232 160, 248 150 C 234 162, 216 170, 201 180 Z" />
            <path d="M200 166 C 196 150, 196 132, 200 118 C 204 132, 204 150, 202 166 Z" />
          </g>
        </motion.g>

        {/* canopy — layered foliage that blooms and sways */}
        <motion.g style={{ scale: canopyScale, opacity: canopyOpacity, transformBox: "view-box", transformOrigin: "200px 158px" }}>
          {/* soft resting glow to draw the eye */}
          <motion.ellipse
            cx="200" cy="102" rx="94" ry="76" fill="url(#fj-glow)"
            animate={reduce ? undefined : { opacity: [0.25, 0.6, 0.25], scale: [0.95, 1.08, 0.95] }}
            transition={reduce ? undefined : { repeat: Infinity, repeatType: "loop", duration: 4.5, ease: "easeInOut" }}
            style={{ transformBox: "fill-box", transformOrigin: "center" }}
          />
          <motion.g
            style={{ transformBox: "view-box", transformOrigin: "200px 158px" }}
            animate={reduce ? undefined : { rotate: [-2.4, 2.4, -2.4], y: [0, -4.5, 0], scale: [1, 1.02, 1] }}
            transition={reduce ? undefined : { repeat: Infinity, repeatType: "loop", duration: 5.5, ease: "easeInOut" }}
          >
            {/* shadowed back layer */}
            <g fill="#2C5836" opacity="0.85" filter="url(#fj-soft)">
              <ellipse cx="200" cy="104" rx="82" ry="66" />
            </g>
            {/* main canopy puffs */}
            <g fill="url(#fj-foliage)">
              <ellipse cx="200" cy="116" rx="56" ry="48" />
              <ellipse cx="162" cy="108" rx="44" ry="40" />
              <ellipse cx="238" cy="108" rx="44" ry="40" />
              <ellipse cx="200" cy="80" rx="48" ry="42" />
              <ellipse cx="172" cy="138" rx="34" ry="30" />
              <ellipse cx="228" cy="138" rx="34" ry="30" />
            </g>
            {/* sun-lit highlights (upper-left) */}
            <g fill="url(#fj-foliage-hi)">
              <ellipse cx="178" cy="86" rx="32" ry="26" />
              <ellipse cx="206" cy="74" rx="22" ry="18" />
              <ellipse cx="158" cy="112" rx="20" ry="16" />
            </g>
            {/* blossoms */}
            {BLOSSOMS.map((b, i) => {
              const Blossom = () => {
                const s = useTransform(mp, [b.t, Math.min(b.t + 0.12, 1)], [0, 1]);
                return (
                  <motion.circle
                    cx={b.cx}
                    cy={b.cy}
                    r={b.r * 1.7}
                    fill="url(#fj-blossom)"
                    style={{ scale: s, opacity: s, transformBox: "fill-box", transformOrigin: "center" }}
                  />
                );
              };
              return <Blossom key={i} />;
            })}
          </motion.g>
        </motion.g>

        {/* birds — kept high in the open sky, clear of the canopy */}
        <motion.g style={{ opacity: birdsOpacity }} stroke="#FDEBD0" strokeWidth="1.6" fill="none" strokeLinecap="round">
          <motion.g
            animate={reduce ? undefined : { x: [-24, 50], y: [0, -5, 0] }}
            transition={reduce ? undefined : { repeat: Infinity, duration: 11, ease: "easeInOut" }}
          >
            <path d="M52 30 q 5 -5 10 0 q 5 -5 10 0" />
            <path d="M96 22 q 4 -4 8 0 q 4 -4 8 0" />
            <path d="M134 28 q 4 -4 8 0 q 4 -4 8 0" />
          </motion.g>
        </motion.g>

        {/* vignette */}
        <rect x="0" y="0" width="400" height="280" fill="url(#fj-sky)" opacity="0" />
        <rect x="0" y="200" width="400" height="80" fill="#05050A" opacity="0.35" />
      </svg>

      {/* warm grain / vignette overlay */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-black/25" />
    </div>
  );
}
